import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FiMail, FiArrowLeft, FiCheckCircle } from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isEmailSent, setIsEmailSent] = useState(false)

    const { showSuccess, showError } = useSnackbarContext()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!email.trim()) {
            showError('Please enter your email address')
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            showError('Please enter a valid email address')
            return
        }

        setIsSubmitting(true)

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 2000))

            // TODO: Implement actual forgot password API call
            console.log('Forgot password request for:', email)

            setIsEmailSent(true)

        } catch (error) {
            console.error('Failed to send reset email:', error)
            showError('Failed to send reset email. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleResend = async () => {
        setIsSubmitting(true)

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1500))

            showSuccess('Password reset email sent again!')

        } catch (error) {
            console.error('Failed to resend email:', error)
            showError('Failed to resend email. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Forgot Password - SideGO</title>
                <meta name="description" content="Reset your password to regain access to your SideGO account" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center">
                        <Link to="/" className="inline-block">
                            <h1 className="text-4xl font-bold text-primary-600 dark:text-primary-400">
                                SideGO
                            </h1>
                        </Link>
                        <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                            Forgot your password?
                        </h2>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {isEmailSent
                                ? "We've sent you a password reset link"
                                : "Enter your email address and we'll send you a link to reset your password"
                            }
                        </p>
                    </div>
                </div>

                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
                        {isEmailSent ? (
                            // Success State
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900 mb-6">
                                    <FiCheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                                </div>

                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Check your email
                                </h3>

                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                    We sent a password reset link to <strong>{email}</strong>
                                </p>

                                <div className="space-y-4">
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        Didn't receive the email? Check your spam folder or try again.
                                    </p>

                                    <button
                                        onClick={handleResend}
                                        disabled={isSubmitting}
                                        className="w-full flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <LoadingSpinner size="sm" className="mr-2" />
                                                Resending...
                                            </>
                                        ) : (
                                            'Resend email'
                                        )}
                                    </button>

                                    <Link
                                        to="/login"
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                    >
                                        Back to sign in
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            // Form State
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Email address
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiMail className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                            placeholder="Enter your email address"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <LoadingSpinner size="sm" className="mr-2" />
                                                Sending reset link...
                                            </>
                                        ) : (
                                            'Send reset link'
                                        )}
                                    </button>
                                </div>

                                <div className="text-center">
                                    <Link
                                        to="/login"
                                        className="inline-flex items-center text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
                                    >
                                        <FiArrowLeft className="mr-2 h-4 w-4" />
                                        Back to sign in
                                    </Link>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Additional Help */}
                    <div className="mt-8 text-center">
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                            <div className="text-sm text-blue-800 dark:text-blue-200">
                                <p className="font-medium mb-2">Need help?</p>
                                <p className="text-xs">
                                    If you're having trouble accessing your account, contact our support team.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
                        <p>
                            Remember your password?{' '}
                            <Link
                                to="/login"
                                className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
                            >
                                Sign in instead
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ForgotPasswordPage